/**
 * Enroll Student — list students created by this school + Add User button/modal.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl, getAuthToken } from '../../services/api';

const API_BASE_URL = getApiBaseUrl();

const SchoolEnrollStudent = () => {
  const { t, i18n } = useTranslation(['common']);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [paymentCustomers, setPaymentCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country: '',
    city: ''
  });

  const fetchPeople = useCallback(async () => {
    try {
      setError(null);
      const token = getAuthToken();
      if (!token) {
        setEnrolledStudents([]);
        setPaymentCustomers([]);
        setError(t('common:school.students.authRequired'));
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/school/people`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setEnrolledStudents(Array.isArray(data.enrolledStudents) ? data.enrolledStudents : []);
        setPaymentCustomers(Array.isArray(data.paymentCustomers) ? data.paymentCustomers : []);
        setError(null);
      } else {
        setEnrolledStudents([]);
        setPaymentCustomers([]);
        setError(data.message || t('common:school.students.failedToLoad'));
      }
    } catch (e) {
      setError(e.message || t('common:school.students.failedToLoad'));
      setEnrolledStudents([]);
      setPaymentCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPeople();
  }, [fetchPeople]);

  const handleOpenAdd = () => {
    setForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      country: '',
      city: ''
    });
    setFormMessage(null);
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name?.trim() || !form.last_name?.trim() || !form.email?.trim() || !form.phone?.trim()) {
      setFormMessage(t('common:school.students.createRequired'));
      return;
    }
    setSubmitting(true);
    setFormMessage(null);
    try {
      const token = getAuthToken();
      if (!token) {
        setFormMessage(t('common:school.students.authRequired'));
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/school/students`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          country: form.country?.trim() || undefined,
          city: form.city?.trim() || undefined
        })
      });
      const data = await res.json();
      if (data.success) {
        setShowModal(false);
        fetchPeople();
      } else {
        setFormMessage(data.message || t('common:school.students.failedToCreate'));
      }
    } catch (e) {
      setFormMessage(e.message || t('common:school.students.failedToCreate'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      const locale = (i18n && i18n.language) ? i18n.language : 'en';
      return new Date(d).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (_) {
      return '—';
    }
  };

  return (
    <div className="school-tab-content school-enroll-student">
      <div className="enroll-header">
        <h1>{t('common:school.enrollStudent')}</h1>
        <button type="button" className="btn-add-user" onClick={handleOpenAdd}>
          <span className="material-icons">person_add</span>
          {t('common:school.addUser')}
        </button>
      </div>
      <p className="enroll-description">{t('common:school.studentsLinkedHint')}</p>
      <p className="enroll-description enroll-hint-secondary">{t('common:school.peopleFromPaymentsHint')}</p>

      {error && <div className="enroll-error">{error}</div>}
      {loading ? (
        <p>{t('common:school.students.loading')}</p>
      ) : enrolledStudents.length === 0 && paymentCustomers.length === 0 && !error ? (
        <div className="enroll-empty">
          <span className="material-icons">school</span>
          <p>{t('common:school.noPeopleYet')}</p>
        </div>
      ) : (
        <>
          <div className="enroll-section">
            <h2 className="enroll-section-title">{t('common:school.sectionEnrolled')}</h2>
            {enrolledStudents.length === 0 ? (
              <p className="enroll-section-empty">{t('common:school.noEnrolledOnly')}</p>
            ) : (
              <div className="enroll-table-wrap">
                <table className="enroll-table">
                  <thead>
                    <tr>
                      <th>{t('common:labels.name')}</th>
                      <th>{t('common:labels.email')}</th>
                      <th>{t('common:school.students.fields.phone')}</th>
                      <th>{t('common:school.students.fields.enrolled')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrolledStudents.map((s) => (
                      <tr key={`e-${s.id}`}>
                        <td>{[s.firstName, s.lastName].filter(Boolean).join(' ') || '—'}</td>
                        <td>{s.email || '—'}</td>
                        <td>{s.phone || '—'}</td>
                        <td>{formatDate(s.enrolledAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="enroll-section">
            <h2 className="enroll-section-title">{t('common:school.sectionPaymentCustomers')}</h2>
            {paymentCustomers.length === 0 ? (
              <p className="enroll-section-empty">{t('common:school.noPaymentCustomersOnly')}</p>
            ) : (
              <div className="enroll-table-wrap">
                <table className="enroll-table">
                  <thead>
                    <tr>
                      <th>{t('common:labels.name')}</th>
                      <th>{t('common:labels.email')}</th>
                      <th>{t('common:school.students.fields.phone')}</th>
                      <th>{t('common:school.paymentRequestsColumn')}</th>
                      <th>{t('common:school.lastPaymentRequest')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentCustomers.map((s) => (
                      <tr key={`p-${s.id}`}>
                        <td>{[s.firstName, s.lastName].filter(Boolean).join(' ') || '—'}</td>
                        <td>{s.email || '—'}</td>
                        <td>{s.phone || '—'}</td>
                        <td>{s.paymentRequestCount}</td>
                        <td>{formatDate(s.lastPaymentRequestAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {showModal && (
        <div className="enroll-modal-overlay" onClick={() => !submitting && setShowModal(false)}>
          <div className="enroll-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enroll-modal-header">
              <h2>{t('common:school.addUser')}</h2>
              <button type="button" className="enroll-modal-close" onClick={() => !submitting && setShowModal(false)} aria-label="Close">
                <span className="material-icons">close</span>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="enroll-form">
              {formMessage && <div className="enroll-form-message">{formMessage}</div>}
              <div className="enroll-form-row">
                <label>{t('common:school.students.fields.firstName')} *</label>
                <input name="first_name" value={form.first_name} onChange={handleChange} required placeholder={t('common:school.students.fields.firstName')} />
              </div>
              <div className="enroll-form-row">
                <label>{t('common:school.students.fields.lastName')} *</label>
                <input name="last_name" value={form.last_name} onChange={handleChange} required placeholder={t('common:school.students.fields.lastName')} />
              </div>
              <div className="enroll-form-row">
                <label>{t('common:labels.email')} *</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="email@example.com" />
              </div>
              <div className="enroll-form-row">
                <label>{t('common:school.students.fields.phone')} *</label>
                <input name="phone" type="tel" value={form.phone} onChange={handleChange} required placeholder={t('common:school.students.fields.phone')} />
              </div>
              <div className="enroll-form-row">
                <label>{t('common:school.students.fields.country')}</label>
                <input name="country" value={form.country} onChange={handleChange} placeholder={t('common:school.students.fields.optional')} />
              </div>
              <div className="enroll-form-row">
                <label>{t('common:school.students.fields.city')}</label>
                <input name="city" value={form.city} onChange={handleChange} placeholder={t('common:school.students.fields.optional')} />
              </div>
              <div className="enroll-form-actions">
                <button type="button" onClick={() => !submitting && setShowModal(false)} disabled={submitting}>{t('common:buttons.cancel')}</button>
                <button type="submit" disabled={submitting}>
                  {submitting ? t('common:school.students.creating') : t('common:school.students.createStudent')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolEnrollStudent;
